/**
 * OutboundDeliveriesPanel — Integration Hub / Envios automáticos
 * -----------------------------------------------------------------------------
 * Painel da aba "Envios" de aplicativos de SAÍDA (transporte de banco de dados).
 *
 * Responsabilidades:
 *   - Ligar/desligar o envio automático de contratos assinados por conexão
 *   - Mostrar o histórico paginado de envios com situação e erro amigável
 *   - Permitir reenvio manual de entregas (pendentes, falhas e já concluídas)
 *
 * Toda a leitura/escrita passa por RPCs (nenhuma consulta direta a tabelas).
 *
 * @version 1.3.0
 * RELEASE: 2026-08-25 v1.3.0 - Tradução de erros técnicos do MySQL para mensagens
 *          amigáveis (FK constraint, table definition changed, column not allowed).
 * RELEASE: 2026-08-24 v1.2.0 - Reenvio liberado para entregas pendentes, com
 *          confirmação antes de interromper a espera automática.
 * RELEASE: 2026-08-24 v1.1.0 - Reenvio liberado para envios já concluídos (provedores por
 *          banco de dados só validam os dados depois), com confirmação antes de reenviar.
 * RELEASE: 2026-08-24 v1.0.0 - Versão inicial (Outbound Delivery Framework).
 */

/**
 * Traduz erros técnicos do MySQL/transporte para mensagens amigáveis ao usuário.
 * Mantém o erro original entre parênteses para diagnóstico técnico.
 */
function translateError(error: string | null | undefined): string | null {
  if (!error) return null;

  // FK constraint: tabela de referência inacessível/vazia no destino
  if (/foreign key constraint fails/i.test(error)) {
    const tableMatch = error.match(/CONSTRAINT\s+`?\w+`?\s+FOREIGN KEY\s*\(`?(\w+)`?\)/i);
    const col = tableMatch?.[1] ?? 'campo';
    return `O sistema de destino recusou o envio: a coluna "${col}" referencia uma tabela que está inacessível ou vazia no banco de destino. Contate o administrador do sistema de destino. (${error})`;
  }

  // Erro 1412: definição da tabela mudou durante a transação
  if (/1412|table definition has changed/i.test(error)) {
    return `O banco de destino estava sendo atualizado no momento do envio. Tente reenviar. (${error})`;
  }

  // Coluna não permitida pela allowlist
  if (/não permitida|not allowed/i.test(error)) {
    const colMatch = error.match(/coluna\s+['"]?(\w+)['"]?/i);
    const col = colMatch?.[1] ?? 'desconhecida';
    return `A coluna "${col}" não está autorizada para gravação no destino. Verifique o mapeamento do aplicativo. (${error})`;
  }

  // Coluna obrigatória vazia
  if (/cannot be null|não pode ser (vazia|null)/i.test(error)) {
    const colMatch = error.match(/column ['"]?(\w+)['"]?/i);
    const col = colMatch?.[1] ?? 'campo';
    return `Um campo obrigatório ("${col}") não foi preenchido. Verifique se o contrato tem todos os dados do cliente e veículo. (${error})`;
  }

  // Erro de conexão
  if (/ECONNREFUSED|ETIMEDOUT|ENOTFOUND|connection refused|connection timed out/i.test(error)) {
    return `Não foi possível conectar ao banco de destino. Verifique se o servidor está online e as credenciais estão corretas. (${error})`;
  }

  return error;
}

import { useState } from 'react';
import { Loader2, RefreshCw, RotateCcw, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { InlineNotice } from '@/components/ui/inline-notice';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ConnectionStatusDot } from '@/components/data/ConnectionStatusDot';
import {
  OUTBOUND_STATUS_LABELS,
  useOutboundDeliveries,
} from '@/hooks/integrations/useOutboundDeliveries';
import type { IntegrationConnection } from '@/hooks/integrations/useIntegrationConnections';

/** Cor da bolinha por situação do envio */
const STATUS_DOT: Record<string, string> = {
  pending: 'bg-amber-500',
  processing: 'bg-sky-500',
  success: 'bg-emerald-500',
  failed: 'bg-destructive',
  skipped: 'bg-muted-foreground',
};

const PAGE_SIZE = 10;

/** Data/hora curta no padrão brasileiro */
function formatDateTime(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface OutboundDeliveriesPanelProps {
  /** Conexões do provider (o envio é configurado por conexão) */
  connections: IntegrationConnection[];
}

export function OutboundDeliveriesPanel({ connections }: OutboundDeliveriesPanelProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  /** v1.2.0 — Entrega pendente ou concluída aguardando confirmação de reenvio */
  const [confirmRetry, setConfirmRetry] = useState<{
    id: string;
    status: 'pending' | 'success';
  } | null>(null);

  /** Conexão em foco: escolhida, padrão ou a primeira */
  const connection =
    connections.find((c) => c.id === selectedId) ??
    connections.find((c) => c.is_default) ??
    connections[0] ??
    null;

  const {
    subscription,
    isLoadingSubscription,
    deliveries,
    totalCount,
    isLoading,
    isFetching,
    refetch,
    saveSubscription,
    retryDelivery,
  } = useOutboundDeliveries({
    connectionId: connection?.id ?? null,
    status: statusFilter === 'all' ? null : statusFilter,
    page,
    pageSize: PAGE_SIZE,
  });

  if (connections.length === 0) {
    return (
      <div className="rounded-lg border py-12 text-center text-sm text-muted-foreground">
        Configure uma conexão com o banco de destino para habilitar os envios.
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <div className="space-y-4">
      {/* ───────── Configuração do envio automático ───────── */}
      <div className="space-y-3 rounded-lg border p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Send className="h-4 w-4 text-primary" />
            Envio automático
          </div>

          {connections.length > 1 && (
            <Select
              value={connection?.id ?? ''}
              onValueChange={(value) => {
                setSelectedId(value);
                setPage(0);
              }}
            >
              <SelectTrigger className="h-8 w-[240px] text-xs">
                <SelectValue placeholder="Escolha a conexão" />
              </SelectTrigger>
              <SelectContent>
                {connections.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                    {c.is_default ? ' (padrão)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          Quando um contrato é assinado por todas as partes, os dados do negócio, do cliente e dos
          veículos são enviados automaticamente para o sistema de destino. O envio é registrado
          abaixo e tentado novamente em caso de falha.
        </p>

        {isLoadingSubscription ? (
          <Skeleton className="h-12 w-full" />
        ) : (
          <div className="flex items-center justify-between rounded-md border px-3 py-2">
            <div>
              <Label className="text-sm font-medium">Enviar contratos assinados</Label>
              <p className="text-xs text-muted-foreground">
                {subscription?.is_active
                  ? `Ativo · ${subscription.success_count} enviados · ${subscription.pending_count} na fila · ${subscription.failed_count} com falha`
                  : 'Desativado: nenhum contrato será enviado.'}
              </p>
            </div>
            <Switch
              checked={Boolean(subscription?.is_active)}
              disabled={saveSubscription.isPending || !connection}
              onCheckedChange={(checked) => saveSubscription.mutate({ isActive: checked })}
            />
          </div>
        )}

        {connection && !connection.is_active && (
          <InlineNotice variant="warning">
            Esta conexão está inativa: os envios ficarão na fila até que ela seja reativada.
          </InlineNotice>
        )}
      </div>

      {/* ───────── Histórico de envios ───────── */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
            Histórico de envios
          </h3>
          <div className="flex items-center gap-2">
            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value);
                setPage(0);
              }}
            >
              <SelectTrigger className="h-8 w-[170px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as situações</SelectItem>
                {Object.entries(OUTBOUND_STATUS_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="xs"
              onClick={() => refetch()}
              disabled={isFetching}
              className="gap-1.5"
            >
              {isFetching ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Atualizar
            </Button>
          </div>
        </div>

        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : deliveries.length === 0 ? (
          <div className="rounded-lg border py-10 text-center text-sm text-muted-foreground">
            Nenhum envio registrado ainda.
          </div>
        ) : (
          <div className="space-y-2">
            {deliveries.map((delivery) => (
              <div
                key={delivery.id}
                className="flex flex-col gap-2 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <ConnectionStatusDot
                      label={OUTBOUND_STATUS_LABELS[delivery.status] ?? delivery.status}
                      dotClassName={STATUS_DOT[delivery.status] ?? 'bg-muted-foreground'}
                    />
                    <span className="truncate text-sm font-medium">
                      {delivery.entity_label ??
                        `Contrato ${delivery.entity_id.slice(0, 8).toUpperCase()}`}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Criado em {formatDateTime(delivery.created_at)}
                    {delivery.finished_at
                      ? ` · Concluído em ${formatDateTime(delivery.finished_at)}`
                      : ''}
                    {delivery.attempts > 0
                      ? ` · Tentativas: ${delivery.attempts}/${delivery.max_attempts}`
                      : ''}
                  </p>
                  {delivery.error && (
                    <p className="break-words text-xs text-destructive">
                      {translateError(delivery.error)}
                    </p>
                  )}
                </div>

                {/*
                  v1.2.0 — Reenvio disponível para pendências, falhas e envios concluídos.
                  O estado "processing" permanece protegido contra execução concorrente.
                */}
                {(delivery.status === 'pending' ||
                  delivery.status === 'failed' ||
                  delivery.status === 'success') && (
                  <Button
                    variant="outline"
                    size="xs"
                    className="gap-1.5"
                    disabled={retryDelivery.isPending}
                    onClick={() => {
                      if (delivery.status === 'pending' || delivery.status === 'success') {
                        setConfirmRetry({ id: delivery.id, status: delivery.status });
                      } else {
                        retryDelivery.mutate(delivery.id);
                      }
                    }}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Reenviar
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {totalCount > PAGE_SIZE && (
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Página {page + 1} de {totalPages} · {totalCount} envios
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="xs"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="xs"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Próxima
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* v1.2.0 — Confirmação antes de antecipar uma pendência ou repetir um envio concluído */}
      <AlertDialog
        open={!!confirmRetry}
        onOpenChange={(open) => {
          if (!open) setConfirmRetry(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reenviar este envio?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmRetry?.status === 'pending'
                ? 'Este envio está aguardando uma tentativa automática. A espera será interrompida e uma nova tentativa será iniciada agora.'
                : 'Este envio já foi concluído. Os dados atualizados serão enviados novamente ao sistema de destino, que fará a validação do seu lado.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmRetry) retryDelivery.mutate(confirmRetry.id);
                setConfirmRetry(null);
              }}
            >
              Reenviar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
